// src/pages/AuthPage.jsx

import React, { useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useLocation } from 'react-router-dom';

import FormField from '../components/ui/FormField';
import { useUser } from '../contexts/UserContext';
import { GoogleOAuthProvider } from '@react-oauth/google';

import {
  EmailIcon,
  Lock,
  UserIcon,
  CheckIcon,
} from '../components/common/IconsComponent';

import GoogleBtn from '../components/social/GoogleBtn';
import FacebookBtn from '../components/social/FacebookBtn';
import SpinnerOverlay from '../components/ui/SpinnerOverlay';


const USER_TYPE_LABELS = {
  teacher: 'מורה',
  student: 'תלמיד',
  school: 'בית ספר',
  supplier: 'מרכז לימוד / ספק',
  admin: 'מנהל מערכת',
};

const TYPE_BADGE_COLORS = {
  teacher: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  student: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  school: 'bg-blue-50 text-blue-700 border-blue-200',
  supplier: 'bg-violet-50 text-violet-700 border-violet-200',
  admin: 'bg-gray-100 text-gray-700 border-gray-200',
};

const FALLBACK_USER_TYPES = [
  { type: 'student', label: 'תלמיד' },
  { type: 'teacher', label: 'מורה' },
  { type: 'school', label: 'בית ספר' },
  { type: 'supplier', label: 'מרכז לימוד' },
];


const Divider = () => (
  <div className="flex items-center gap-3 my-1">
    <div className="flex-1 h-px bg-gray-100" />
    <span className="text-xs text-gray-400 select-none">או</span>
    <div className="flex-1 h-px bg-gray-100" />
  </div>
);


const FooterLink = ({ onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className="text-sm text-indigo-600 hover:text-indigo-800 hover:underline transition-colors"
  >
    {children}
  </button>
);


const SubmitButton = ({ loading, label, loadingLabel, disabled }) => (
  <button
    type="submit"
    disabled={disabled || loading}
    className="w-full py-2.5 sm:py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm sm:text-base font-semibold rounded-xl shadow-sm hover:opacity-90 active:opacity-80 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
  >
    {loading ? loadingLabel : label}
  </button>
);


const SocialButtons = ({ disabled, mode, showAlert }) => (
  <>
    <div className="space-y-2.5">
      <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
        <GoogleBtn
          disabled={disabled}
          mode={mode}
          showAlert={showAlert}
        />
      </GoogleOAuthProvider>

      <FacebookBtn
        disabled={disabled}
        mode={mode}
        showAlert={showAlert}
      />
    </div>

    <Divider />
  </>
);


const Shell = ({
  title,
  children,
  footer,
  showSocial,
  disableSocial,
  allowSocial,
  mustSelectType,
  isAdmin,
  isLoading,
  currentView,
  effectiveUserType,
  userTypeLabel,
  setUserType,
  showAlert,
}) => (
  <div
    className="min-h-screen bg-gray-100 flex flex-col items-center justify-center px-2 sm:px-4 py-8"
    dir="rtl"
  >
    {isLoading && (
      <SpinnerOverlay
        variant="glow"
        text="נא להמתין"
        dark={false}
        blur={false}
      />
    )}

    <div
      aria-hidden
      className="pointer-events-none fixed inset-0 overflow-hidden"
    >
      <div className="absolute -top-32 -right-32 w-72 h-72 rounded-full bg-indigo-100/60 blur-3xl" />
      <div className="absolute -bottom-32 -left-32 w-80 h-80 rounded-full bg-purple-100/50 blur-3xl" />
    </div>

    <div className="relative z-10 w-full max-w-md">
      <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
        <div className="h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-blue-500" />

        <div className="p-5 sm:p-7">
          <div className="text-center mb-5 sm:mb-6">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
              {title}
            </h2>

            {isAdmin ? (
              <span
                className={`inline-block mt-2 px-3 py-0.5 rounded-full border text-xs font-medium ${TYPE_BADGE_COLORS.admin}`}
              >
                {userTypeLabel || 'מנהל מערכת'}
              </span>
            ) : userTypeLabel ? (
              <span
                className={`inline-block mt-2 px-3 py-0.5 rounded-full border text-xs font-medium ${
                  TYPE_BADGE_COLORS[effectiveUserType] ?? TYPE_BADGE_COLORS.admin
                }`}
              >
                {userTypeLabel}
              </span>
            ) : (
              <div className="mt-3 space-y-3">
                <p className="text-xs text-red-500">
                  יש לבחור סוג משתמש או ארגון במסך הקודם.
                </p>

                <div className="grid grid-cols-2 gap-2">
                  {FALLBACK_USER_TYPES.map(({ type, label }) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setUserType(type)}
                      className="px-3 py-2 text-xs rounded-lg border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-colors"
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {showSocial && allowSocial && (
            <SocialButtons
              disabled={disableSocial || mustSelectType}
              mode={currentView}
              showAlert={showAlert}
            />
          )}

          {children}

          {footer && (
            <div className="mt-5 flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5">
              {footer}
            </div>
          )}
        </div>
      </div>

      <p className="mt-5 text-center text-xs text-gray-400">
        www.doroosonline.co.il
      </p>
    </div>
  </div>
);


const AuthPage = () => {
  const location = useLocation();

  const [currentView, setCurrentView] = useState('login');
  const [submitted, setSubmitted] = useState(false);
  // Address the reset link was sent to (shown in the confirmation).
  const [sentEmail, setSentEmail] = useState('');

  const {
    login,
    signup,
    isLoading,
    showAlert,
    clearAlert,
    userType,
    setUserType,
    requestPasswordReset,
  } = useUser();


  const isAdminRoute = useMemo(() => {
    const path = (location?.pathname || '').toLowerCase();
    return path === '/admin' || path.startsWith('/admin/');
  }, [location?.pathname]);

  const effectiveUserType = isAdminRoute ? 'admin' : userType;
  const isAdmin = effectiveUserType === 'admin';
  const mustSelectType = !effectiveUserType && !isAdmin;
  const allowSocial = !isAdmin;
  const userTypeLabel = USER_TYPE_LABELS[effectiveUserType] ?? '';

  const shellShared = {
    allowSocial,
    mustSelectType,
    isAdmin,
    isLoading,
    currentView,
    effectiveUserType,
    userTypeLabel,
    setUserType,
    showAlert,
  };


  useEffect(() => {
    if (isAdminRoute && setUserType) {
      try {
        setUserType('admin');
      } catch {
        // Ignore
      }
    }
  }, [isAdminRoute, setUserType]);


  useEffect(() => {
    if (isAdmin && currentView === 'signup') {
      setCurrentView('login');
    }
  }, [isAdmin, currentView]);


  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'auto',
    });
  }, [location.pathname, currentView]);


  useEffect(() => {
    if (clearAlert) {
      try {
        clearAlert();
      } catch {
        // Ignore
      }
    }
  }, [currentView, clearAlert]);


  const loginSchema = useMemo(
    () =>
      Yup.object({
        email: Yup.string()
          .email('כתובת מייל לא תקינה')
          .required('כתובת מייל נדרשת'),

        password: Yup.string()
          .min(6, 'לפחות 6 תווים')
          .required('סיסמה נדרשת'),
      }),
    []
  );


  const signupSchema = useMemo(
    () =>
      Yup.object({
        fullName: Yup.string()
          .min(2, 'לפחות 2 תווים')
          .required('שם מלא נדרש'),

        email: Yup.string()
          .email('כתובת מייל לא תקינה')
          .required('כתובת מייל נדרשת'),

        password: Yup.string()
          .min(6, 'לפחות 6 תווים')
          .required('סיסמה נדרשת'),

        confirmPassword: Yup.string()
          .oneOf([Yup.ref('password'), null], 'הסיסמאות אינן תואמות')
          .required('אימות סיסמה נדרש'),

        termsAccepted: Yup.boolean()
          .oneOf([true], 'יש לאשר את התנאים')
          .required(),
      }),
    []
  );


  const forgotSchema = useMemo(
    () =>
      Yup.object({
        email: Yup.string()
          .email('כתובת מייל לא תקינה')
          .required('כתובת מייל נדרשת'),
      }),
    []
  );


  const openForgot = () => {
    setSubmitted(false);
    setSentEmail('');
    forgotFormik.resetForm();
    setCurrentView('forgot');
  };

  const loginFormik = useFormik({
    initialValues: {
      email: '',
      password: '',
      rememberMe: false,
    },

    validationSchema: loginSchema,

    onSubmit: async (values, { setSubmitting }) => {
      try {
        if (mustSelectType) {
          showAlert('יש לבחור סוג משתמש או ארגון לפני התחברות.', 'error');
          return;
        }

        await login(
          values.email,
          values.password,
          'normal',
          effectiveUserType
        );

        showAlert('התחברת בהצלחה!', 'success');
      } catch (error) {
        showAlert(
          error?.message || 'שגיאה בהתחברות',
          'error'
        );
      } finally {
        setSubmitting(false);
      }
    },
  });


  const signupFormik = useFormik({
    initialValues: {
      fullName: '',
      email: '',
      password: '',
      confirmPassword: '',
      termsAccepted: false,
    },

    validationSchema: signupSchema,

    onSubmit: async (values, { setSubmitting }) => {
      try {
        if (!effectiveUserType) {
          showAlert('יש לבחור סוג משתמש או ארגון לפני הרשמה.', 'error');
          return;
        }

        if (isAdmin) {
          showAlert('לא ניתן ליצור חשבון אדמין דרך הרשמה.', 'error');
          return;
        }

        await signup(
          {
            fullName: values.fullName,
            email: values.email,
            password: values.password,
          },
          effectiveUserType
        );

        showAlert('נרשמת בהצלחה!', 'success');
      } catch (error) {
        showAlert(
          error?.message || 'שגיאה בהרשמה',
          'error'
        );
      } finally {
        setSubmitting(false);
      }
    },
  });


  const forgotFormik = useFormik({
    initialValues: {
      email: '',
    },

    validationSchema: forgotSchema,

    onSubmit: async (values, { setSubmitting }) => {
      try {
        await requestPasswordReset(values.email);

        setSentEmail(values.email.trim());
        setSubmitted(true);

        showAlert(
          'אם הכתובת קיימת – נשלח קישור לשחזור.',
          'success'
        );
      } catch (error) {
        showAlert(
          error?.message || 'שגיאה בשליחה',
          'error'
        );
      } finally {
        setSubmitting(false);
      }
    },
  });


  if (currentView === 'login') {
    return (
      <Shell
        {...shellShared}
        title={isAdmin ? 'כניסת מנהל מערכת' : 'כניסה לפלטפורמה'}
        showSocial
        footer={
          isAdmin ? (
            <FooterLink onClick={openForgot}>
              שכחת סיסמה?
            </FooterLink>
          ) : (
            <>
              <FooterLink onClick={() => setCurrentView('signup')}>
                אין לך חשבון? הירשם
              </FooterLink>

              <span className="text-gray-200 select-none">|</span>

              <FooterLink onClick={openForgot}>
                שכחת סיסמה?
              </FooterLink>
            </>
          )
        }
      >
        <form
          onSubmit={loginFormik.handleSubmit}
          className="space-y-4"
        >
          <FormField
            label="כתובת מייל"
            name="email"
            type="email"
            value={loginFormik.values.email}
            onChange={loginFormik.handleChange}
            onBlur={loginFormik.handleBlur}
            placeholder="הזן כתובת מייל"
            icon={<EmailIcon />}
            required
            error={
              loginFormik.touched.email &&
              loginFormik.errors.email
            }
          />

          <FormField
            label="סיסמה"
            name="password"
            type="password"
            value={loginFormik.values.password}
            onChange={loginFormik.handleChange}
            onBlur={loginFormik.handleBlur}
            placeholder="הזן סיסמה"
            icon={<Lock />}
            required
            error={
              loginFormik.touched.password &&
              loginFormik.errors.password
            }
          />

          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
            <input
              type="checkbox"
              name="rememberMe"
              checked={loginFormik.values.rememberMe}
              onChange={loginFormik.handleChange}
              className="rounded border-gray-300 text-indigo-600"
            />

            זכור אותי
          </label>

          <SubmitButton
            loading={
              isLoading ||
              loginFormik.isSubmitting
            }
            label="התחבר"
            loadingLabel="מתחבר..."
            disabled={mustSelectType}
          />
        </form>
      </Shell>
    );
  }


  if (currentView === 'signup' && !isAdmin) {
    return (
      <Shell
        {...shellShared}
        title="יצירת חשבון חדש"
        showSocial
        footer={
          <FooterLink onClick={() => setCurrentView('login')}>
            כבר יש לך חשבון? התחבר
          </FooterLink>
        }
      >
        <form
          onSubmit={signupFormik.handleSubmit}
          className="space-y-4"
        >
          <FormField
            label="שם מלא"
            name="fullName"
            value={signupFormik.values.fullName}
            onChange={signupFormik.handleChange}
            onBlur={signupFormik.handleBlur}
            placeholder="הזן שם מלא"
            icon={<UserIcon />}
            required
            error={
              signupFormik.touched.fullName &&
              signupFormik.errors.fullName
            }
          />

          <FormField
            label="כתובת מייל"
            name="email"
            type="email"
            value={signupFormik.values.email}
            onChange={signupFormik.handleChange}
            onBlur={signupFormik.handleBlur}
            placeholder="הזן כתובת מייל"
            icon={<EmailIcon />}
            required
            error={
              signupFormik.touched.email &&
              signupFormik.errors.email
            }
          />

          <FormField
            label="סיסמה"
            name="password"
            type="password"
            value={signupFormik.values.password}
            onChange={signupFormik.handleChange}
            onBlur={signupFormik.handleBlur}
            placeholder="הזן סיסמה"
            icon={<Lock />}
            required
            error={
              signupFormik.touched.password &&
              signupFormik.errors.password
            }
          />

          <FormField
            label="אימות סיסמה"
            name="confirmPassword"
            type="password"
            value={signupFormik.values.confirmPassword}
            onChange={signupFormik.handleChange}
            onBlur={signupFormik.handleBlur}
            placeholder="הזן שוב"
            icon={<CheckIcon />}
            required
            error={
              signupFormik.touched.confirmPassword &&
              signupFormik.errors.confirmPassword
            }
          />

          <label className="flex items-start gap-2 text-sm text-gray-600 cursor-pointer select-none">
            <input
              type="checkbox"
              name="termsAccepted"
              checked={signupFormik.values.termsAccepted}
              onChange={signupFormik.handleChange}
              onBlur={signupFormik.handleBlur}
              className="mt-0.5 rounded border-gray-300 text-indigo-600 flex-shrink-0"
            />

            <span>
              אני מסכים/ה ל
              <a
                href="/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-indigo-600 hover:underline mx-1"
              >
                תנאים והגבלות
              </a>
            </span>
          </label>

          {signupFormik.touched.termsAccepted &&
            signupFormik.errors.termsAccepted && (
              <p className="text-xs text-red-500 -mt-2">
                {signupFormik.errors.termsAccepted}
              </p>
            )}

          <SubmitButton
            loading={
              isLoading ||
              signupFormik.isSubmitting
            }
            label="הרשם"
            loadingLabel="נרשם..."
            disabled={mustSelectType}
          />
        </form>
      </Shell>
    );
  }


  return (
    <Shell
      {...shellShared}
      title="שחזור סיסמה"
      footer={
        submitted ? null : (
          <FooterLink onClick={() => setCurrentView('login')}>
            נזכרת בסיסמה? חזור להתחברות
          </FooterLink>
        )
      }
    >
      {submitted ? (
        <div className="text-center">
          <div className="p-5 bg-indigo-50 border border-indigo-100 rounded-xl">
            <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center mx-auto mb-3">
              <svg
                className="w-6 h-6 text-indigo-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>

            <p className="text-sm font-semibold text-gray-800 mb-1">
              קישור לשחזור הסיסמה נשלח לכתובת המייל:
            </p>

            <p
              dir="ltr"
              className="text-sm font-bold text-indigo-700 break-all mb-3"
            >
              {sentEmail}
            </p>

            <p className="text-xs text-gray-500 leading-relaxed">
              יש ללחוץ על הקישור שבמייל כדי לבחור סיסמה חדשה.
              <br />
              לא קיבלת? כדאי לבדוק גם בתיקיית הספאם.
            </p>
          </div>

          <button
            type="button"
            onClick={() => {
              setSubmitted(false);
              setSentEmail('');
              forgotFormik.resetForm();
              setCurrentView('login');
            }}
            className="mt-5 w-full py-2.5 sm:py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm sm:text-base font-semibold rounded-xl shadow-sm hover:opacity-90 active:opacity-80 transition-all"
          >
            חזרה להתחברות
          </button>
        </div>
      ) : (
        <form
          onSubmit={forgotFormik.handleSubmit}
          className="space-y-4"
        >
          <FormField
            label="כתובת מייל"
            name="email"
            type="email"
            value={forgotFormik.values.email}
            onChange={forgotFormik.handleChange}
            onBlur={forgotFormik.handleBlur}
            placeholder="הזן כתובת מייל"
            icon={<EmailIcon />}
            required
            error={
              forgotFormik.touched.email &&
              forgotFormik.errors.email
            }
          />

          <SubmitButton
            loading={
              isLoading ||
              forgotFormik.isSubmitting
            }
            label="שלח קישור לשחזור"
            loadingLabel="שולח..."
          />
        </form>
      )}
    </Shell>
  );
};


export default AuthPage;